import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminBadgeEmbeds } from "../lib/api";

// Operator-facing outreach helper (option e). Copy-paste templates the team
// sends founders to get them to embed the badge. No automation — manual send.
const EMAIL_TEMPLATE = `Subject: You're featured on TerminalValue.io

Hi {{name}},

{{tool}} is listed on TerminalValue.io — the investing tools index:
https://terminalvalue.io/tool/{{slug}}

You can embed a live "Featured on TerminalValue.io" badge on your site. It
shows your real-time upvote count and links back to your profile. The more
votes you gather, the higher you climb our homepage ranking.

Grab your badge here (no login):
https://terminalvalue.io/badge

Takes 30 seconds — copy, paste, done.

— TerminalValue.io`;

const DM_TEMPLATE = `hey {{name}} — {{tool}} is featured on TerminalValue.io (the investing tools index). you can embed a live badge that shows your upvote count + links back to your page. grab it here, no login: https://terminalvalue.io/badge`;

function CopyBlock({ label, text, testid }) {
  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[11px] uppercase tracking-wide text-[#888]">{label}</div>
        <button
          type="button"
          className="tv-action-link"
          data-testid={`${testid}-copy`}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              toast.success("copied");
            } catch {
              toast.error("could not copy");
            }
          }}
        >
          [copy]
        </button>
      </div>
      <pre
        className="mt-1 p-2 border border-black bg-[#FBF8EE] text-[11px] overflow-x-auto whitespace-pre-wrap"
        data-testid={testid}
      >
        {text}
      </pre>
    </div>
  );
}

export default function AdminBadges() {
  const [slug, setSlug] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => {
      adminBadgeEmbeds()
        .then((d) => {
          if (alive) {
            setData(d);
            setLoading(false);
          }
        })
        .catch(() => {
          if (alive) setLoading(false);
        });
    });
    return () => {
      alive = false;
    };
  }, []);

  const embeds = data?.embeds || [];
  const copies = data?.copies || [];

  return (
    <div data-testid="admin-badges">
      {/* Who is embedding the badge (referrer-tracked) */}
      <div className="border border-black p-3" data-testid="admin-badge-embeds">
        <div className="text-[13px] font-bold">where the badge is embedded</div>
        <p className="text-[11px] text-[#888] mt-1 m-0">
          Each badge load reports the embedding site via its referrer. Self &
          cached loads aren’t counted, so real counts may lag a little.
        </p>

        {loading ? (
          <div className="mt-3 text-[12px] text-[#888]">loading…</div>
        ) : embeds.length === 0 ? (
          <div className="mt-3 text-[12px] text-[#888]" data-testid="admin-badge-embeds-empty">
            No external embeds detected yet. Once a founder puts the badge on
            their site, the domain shows up here.
          </div>
        ) : (
          <>
            <div className="mt-2 text-[11px] text-[#666]">
              {data.distinct_domains} domain(s) · {data.tools_embedded} tool(s) ·{" "}
              {data.total_loads} total loads
            </div>
            <table className="mt-2 w-full text-[12px] border-collapse" data-testid="admin-badge-embeds-table">
              <thead>
                <tr className="text-left border-b border-black">
                  <th className="py-1 pr-2 font-bold">tool</th>
                  <th className="py-1 pr-2 font-bold">embedded on</th>
                  <th className="py-1 pr-2 font-bold text-right">loads</th>
                  <th className="py-1 font-bold">last seen</th>
                </tr>
              </thead>
              <tbody>
                {embeds.map((e) => (
                  <tr key={`${e.slug}-${e.host}`} className="border-b border-[#eee]" data-testid={`admin-badge-embed-${e.slug}-${e.host}`}>
                    <td className="py-1 pr-2">
                      <a href={`/tool/${e.slug}`} target="_blank" rel="noopener noreferrer" className="tv-link">
                        {e.title}
                      </a>
                    </td>
                    <td className="py-1 pr-2">
                      <a href={`https://${e.host}`} target="_blank" rel="noopener noreferrer" className="tv-link break-all">
                        {e.host}
                      </a>
                      {Array.isArray(e.pages) && e.pages.length > 0 && (
                        <ul className="mt-1 space-y-0.5" data-testid={`admin-badge-embed-pages-${e.slug}-${e.host}`}>
                          {[...e.pages].sort((a, b) => (b.count || 0) - (a.count || 0)).map((p) => (
                            <li key={p.path} className="text-[11px] text-[#666] flex gap-2">
                              <a href={`https://${e.host}${p.path}`} target="_blank" rel="noopener noreferrer" className="tv-link break-all" data-testid={`admin-badge-embed-page-${e.slug}-${e.host}-${p.path}`}>
                                {p.path}
                              </a>
                              <span className="tabular-nums text-[#999]">×{p.count}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{e.count}</td>
                    <td className="py-1 text-[#666]">{(e.last_seen || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Embed-code copies (install intent, before the badge goes live) */}
      <div className="border border-black p-3 mt-3" data-testid="admin-badge-copies">
        <div className="text-[13px] font-bold">embed code copied</div>
        <p className="text-[11px] text-[#888] mt-1 m-0">
          How many times founders copied a tool’s badge snippet — install intent,
          even before it appears on a site above.
        </p>
        {loading ? (
          <div className="mt-3 text-[12px] text-[#888]">loading…</div>
        ) : copies.length === 0 ? (
          <div className="mt-3 text-[12px] text-[#888]" data-testid="admin-badge-copies-empty">
            No badge copies yet.
          </div>
        ) : (
          <>
            <div className="mt-2 text-[11px] text-[#666]">
              {data.total_copies} total copies · {copies.length} tool(s)
            </div>
            <table className="mt-2 w-full text-[12px] border-collapse" data-testid="admin-badge-copies-table">
              <thead>
                <tr className="text-left border-b border-black">
                  <th className="py-1 pr-2 font-bold">tool</th>
                  <th className="py-1 pr-2 font-bold text-right">copies</th>
                  <th className="py-1 font-bold">last copied</th>
                </tr>
              </thead>
              <tbody>
                {copies.map((c) => (
                  <tr key={c.slug} className="border-b border-[#eee]" data-testid={`admin-badge-copy-${c.slug}`}>
                    <td className="py-1 pr-2">
                      <a href={`/tool/${c.slug}`} target="_blank" rel="noopener noreferrer" className="tv-link">
                        {c.title}
                      </a>
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{c.count}</td>
                    <td className="py-1 text-[#666]">{(c.last_copied || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <p className="text-[13px] text-[#444] m-0 mt-5">
        Founders embed a live badge from{" "}
        <a href="/badge" target="_blank" rel="noopener noreferrer" className="tv-link">/badge</a>{" "}
        (no login). Use the templates below to nudge them. Replace{" "}
        <code>{"{{name}}"}</code>, <code>{"{{tool}}"}</code> and{" "}
        <code>{"{{slug}}"}</code> before sending.
      </p>

      {/* Quick badge-URL lookup for a slug */}
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-wide text-[#888]">badge url for a slug</div>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value.trim())}
          placeholder="tool slug, e.g. alphasense"
          className="mt-1 w-full border border-black px-3 py-2 text-[13px] outline-none focus:bg-[#FBF8EE]"
          data-testid="admin-badges-slug"
        />
        {slug && (
          <div className="mt-2 text-[12px]" data-testid="admin-badges-url">
            <a
              href={`/api/badges/${slug}.svg`}
              target="_blank"
              rel="noopener noreferrer"
              className="tv-link break-all"
            >
              /api/badges/{slug}.svg
            </a>
          </div>
        )}
      </div>

      <CopyBlock label="email template" text={EMAIL_TEMPLATE} testid="admin-badges-email" />
      <CopyBlock label="dm / short template" text={DM_TEMPLATE} testid="admin-badges-dm" />
    </div>
  );
}
